import { SimModeProvider } from "@shared/sim";
import { useWalletModal } from "@shared/wallet";
import posthog from "posthog-js";
import React from "react";

import { useAccessControl } from "@/hooks/access/useAccessControl";
import { useStakeTransaction } from "@/hooks/staking/useStakeTransaction";
import { useStartUnstakeTransaction } from "@/hooks/staking/useStartUnstakeTransaction";
import { useUnstakeTransaction } from "@/hooks/staking/useUnstakeTransaction";
import { useReportingLiveApy } from "@/hooks/stats/useReportingLiveApy";
import { useSwap } from "@/hooks/swap";

/**
 * Supplies the current Data API reporting rate
 * into the single `{ apy, formattedApy }` shape the swap widget's
 * `useAdvertisedApyOrchestration` consumes. Injected so the widget
 * bundle never statically imports the reporting hooks
 * (and therefore never pulls `@tanstack/react-query` or
 * `@solomon-apps/config`) on the sim path.
 */
function useResolvedApy(): { apy: number | null; formattedApy: string } {
    const { apy, formattedApy } = useReportingLiveApy();
    return { apy, formattedApy };
}

/** Real product-analytics capture, injected so widgets don't import posthog-js. */
const capture = (event: string, properties?: Record<string, unknown>): void => {
    posthog.capture(event, properties);
};

/**
 * apps/main-side wiring for the sim-mode orchestrators.
 *
 * Mounts a `<SimModeProvider>` with the real orchestration hooks
 * injected via `realHooks`. Widgets call `useSwapOrchestration` /
 * `useStakeOrchestration` / etc. and the orchestrator resolves to
 * these real hooks when no sim scenario is active. Without this
 * provider mounted (or its equivalent realHooks injection), the
 * orchestrators throw on first call.
 *
 * `realHooks` is memoized at module scope — the hook references
 * themselves are stable, so SimModeContext value identity stays
 * stable for the widget lifetime (rules-of-hooks contract).
 *
 * Wrap any subtree that renders BuyWidget / StakeWidget / etc.
 * Idempotent — nesting with a downstream scenario-driven
 * SimModeProvider works fine; the inner provider just overrides
 * `scenario` and `wallet` on the same context.
 */

declare module "@shared/sim" {
    interface SimRealHooks {
        useStakeTransaction?: typeof useStakeTransaction;
        useStartUnstakeTransaction?: typeof useStartUnstakeTransaction;
        useUnstakeTransaction?: typeof useUnstakeTransaction;
        useAccessControl?: typeof useAccessControl;
        useWalletModal?: typeof useWalletModal;
        useResolvedApy?: typeof useResolvedApy;
        capture?: typeof capture;
    }
}

const REAL_HOOKS = {
    useSwap,
    useStakeTransaction,
    useStartUnstakeTransaction,
    useUnstakeTransaction,
    useAccessControl,
    useWalletModal,
    useResolvedApy,
    capture
};

export function SimRealHooksProvider({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    return <SimModeProvider realHooks={REAL_HOOKS}>{children}</SimModeProvider>;
}
